import { configureStore } from '@reduxjs/toolkit';
import { persistReducer, persistStore } from 'redux-persist';
import { encryptTransform } from 'redux-persist-transform-encrypt';
import storage from 'redux-persist/lib/storage';

// Import all slices
import adminReducer from './slices/adminSlice';
import clerkAuthReducer from './slices/clerkAuthSlice';
import documentsReducer from './slices/documentsSlice';
import formsReducer from './slices/formsSlice';
import notificationsReducer from './slices/notificationsSlice';
import requestsReducer from './slices/requestsSlice';
import uiReducer from './slices/uiSlice';
import vendorsReducer from './slices/vendorsSlice';

// Middleware
import { errorHandlingMiddleware } from './middleware/errorHandling';
import { performanceMiddleware } from './middleware/performance';
import { stateLoggerMiddleware } from './middleware/stateLogger';

// Root reducer
import { combineReducers } from '@reduxjs/toolkit';

const rootReducer = combineReducers({
  admin: adminReducer,
  clerkAuth: clerkAuthReducer, // Clerk-based auth
  ui: uiReducer,
  forms: formsReducer,
  requests: requestsReducer,
  documents: documentsReducer,
  notifications: notificationsReducer,
  vendors: vendorsReducer,
});

// Persist config - only persist necessary data
const persistConfig = {
  key: 'ezhoa-root',
  storage,
  whitelist: ['ui', 'requests', 'vendors', 'documents', 'notifications'], // Persist most data for offline access
  blacklist: ['clerkAuth', 'admin', 'forms'], // Don't persist auth/admin states or form data
  transforms: [
    encryptTransform({
      secretKey: import.meta.env.VITE_REDUX_SECRET || 'ezhoa-secret-key-2024',
      onError: (error) => {
        console.error('Redux persist encryption error:', error);
      },
    }),
  ],
};

// Create persisted reducer
const persistedReducer = persistReducer(persistConfig, rootReducer);

// Configure store with proper middleware
export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE'],
        ignoredActionPaths: ['meta.arg', 'payload.timestamp'],
        ignoredPaths: ['items.dates'],
      },
      immutableCheck: {
        warnAfter: 128,
      },
      thunk: {
        extraArgument: {
          api: import.meta.env.VITE_API_BASE || 'http://localhost:3001/api',
        },
      },
    })
      .concat(errorHandlingMiddleware)
      .concat(performanceMiddleware)
      .concat(stateLoggerMiddleware),
  devTools: import.meta.env.MODE !== 'production' && {
    name: 'EZHOA Store',
    trace: true,
    traceLimit: 25,
    actionSanitizer: (action) => ({
      ...action,
      payload: action.type.includes('auth') ? '<<SANITIZED>>' : action.payload,
    }),
  },
});

export const persistor = persistStore(store);

// Export types
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

// Re-export all actions and selectors
export * from './slices/adminSlice';
export * from './slices/clerkAuthSlice';
export * from './slices/uiSlice';
export * from './slices/formsSlice';
export * from './slices/requestsSlice';
export * from './slices/documentsSlice';
export * from './slices/notificationsSlice';
export * from './slices/vendorsSlice';
